// @ts-strict-ignore
import { useEffect } from "react";

import type { CompanySettings } from "@dashboardApi/apis/company";

import { useBrazeDashboardConfig } from "@services/brazeDashboardConfig";
import { useDashboardApiQuery } from "@services/useDashboardApi";
import { useFeatureOn } from "@services/useFeatureOn";

/**
 * Retrieve the current company's information.
 *
 * This is guaranteed to be available immediately, because the placeholder data is the current company as
 * populated in Braze's dashboard config.
 *
 * As endpoints are added that update the company, they can invalidate the cache, guaranteeing the company is
 * only ever a few seconds out of date.
 */
// eslint-disable-next-line local-eslint-rules/enforce-api-query-hook-naming-convention
export const useCompany = () => {
  const featureOn = useFeatureOn();
  const user = useBrazeDashboardConfig("user");
  // eslint-disable-next-line local-eslint-rules/enforce-dashboard-api-query-return
  const info = useDashboardApiQuery("/companies/:company_id/settings", {
    params: [user.company._id],
    options: {
      placeholderData: { settings: user.company },
      select: ({ settings }): CompanySettings => settings,
      keepPreviousData: true,
      ...(featureOn("use_company_notify_on_change_props") && {
        notifyOnChangeProps: ["data", "error"],
      }),
    },
  });

  useEffect(() => {
    // preserve the previous value if they match
    if (!!info?.data && JSON.stringify(user.company) !== JSON.stringify(info?.data)) {
      user.company = info?.data;
    }
  }, [user.company, info?.data, user]);
  return {
    ...info,
    // ensure that on error, we fall back to the dashboard config's company instance
    data: info.data || user.company,
  };
};
